import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Label from 'LuxKit/Label/Label'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import LuxPlusBookingProtectionLabelLabel from 'luxPlus/components/LuxPlusBookingProtectionLabel'
import LuxPlusInsuranceLabel from 'luxPlus/components/LuxPlusInsuranceLabel'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import React, { MouseEventHandler, useCallback, useContext } from 'react'
import LuxPlusMonthInfoModal from './LuxPlusMonthInfoModal'

const LOGO_HEIGHT = 9

interface Props extends Utils.PropsWithDataAttributes {
  type: App.LuxPlusLabelType
  saveAmount?: number
  hideSaveAmountPlus?: boolean
  currency?: string
  description?: string
}

function LuxPlusLabelMonth({
  currency,
  description,
  type,
  saveAmount,
  hideSaveAmountPlus,
  ...rest
}: Props) {
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const showModal = useContext(ModalContext)
  const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()
    showModal(<LuxPlusMonthInfoModal />)
  }, [showModal])

  if (!luxPlusEnabled) return null

  const saveAmountSuffix = hideSaveAmountPlus ? '' : '+'

  return <Clickable onClick={handleClick} data-testid="lux-plus-month-label-clickable">
    <Label
      data-testid="lux-plus-month-label"
      {...rest}
      kind="primary"
      variant="lux-plus-month"
      endIcon={<LineInfoCircleIcon />}
    >
      {type === 'bonus-inclusions' && !description && <>Bonus inclusions with <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> Month</>}
      {type === 'bonus-inclusions' && !!description && <>{description} with  <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> Month</>}
      {type === 'member' && <><LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> member</>}
      {type === 'only-logo' && <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />}
      {type === 'early-access-offer' && <>Early access with <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> Month</>}
      {type === 'member-only-offer' && <><LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> Month Member-only offer</>}
      {type === 'member-price-offer' && !!saveAmount && <>Save <FormatCurrency format="roundedDollar" value={saveAmount} />{saveAmountSuffix} with <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> Month</>}
      {type === 'member-price-offer-lpp' && !!saveAmount && <>Save <FormatCurrency format="roundedDollar" value={saveAmount} />{saveAmountSuffix} with <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /></>}
      {type === 'member-price-offer-tour' && !!saveAmount && <>Save <FormatCurrency format="roundedDollar" value={saveAmount} />{saveAmountSuffix}/person with <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /></>}
      {type === 'options-available-tour' && <><LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> options available</>}
      {type === 'plus' && <LuxPlusLogo colour="inherit" type="plus-only" height={LOGO_HEIGHT} />}
      {type === 'plus-preview' && <><LuxPlusLogo colour="inherit" type="plus-only" height={LOGO_HEIGHT} /> preview</>}
      {type === 'insurance' && !!saveAmount && <LuxPlusInsuranceLabel saveAmount={saveAmount} logoHeight={LOGO_HEIGHT} />}
      {type === 'booking-protection' && !!saveAmount && <LuxPlusBookingProtectionLabelLabel saveAmount={saveAmount} logoHeight={LOGO_HEIGHT} />}
      {type === 'savings' && !!saveAmount && <>Extra <FormatCurrency format="roundedDollar" value={saveAmount} /> saved with <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /></>}
      {type === 'member-inclusions' && <>Member inclusions with <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /></>}
    </Label>
  </Clickable>
}

export default LuxPlusLabelMonth
